import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import { Box, Paper, Typography, Chip, IconButton } from "@mui/material";
import {
  Warning,
  Construction,
  CarCrash,
  Traffic,
  Close,
} from "@mui/icons-material";
import L from "leaflet";
import { config } from "../config";

// Fix für Leaflet Icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

// Polyline decoder für GraphHopper encoded strings
function decodePolyline(encoded) {
  const poly = [];
  let index = 0,
    len = encoded.length;
  let lat = 0,
    lng = 0;

  while (index < len) {
    let b,
      shift = 0,
      result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlat = result & 1 ? ~(result >> 1) : result >> 1;
    lat += dlat;

    shift = 0;
    result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlng = result & 1 ? ~(result >> 1) : result >> 1;
    lng += dlng;

    poly.push([lat / 1e5, lng / 1e5]);
  }
  return poly;
}

// Komponente für Multi-Route-Anzeige
function MultiRouteDisplay({
  routes,
  recommendedRoute,
  selectedRoute,
  routeData,
}) {
  const map = useMap();

  useEffect(() => {
    const routeLines = [];

    if (routes && typeof routes === "object") {
      let bounds = null;

      Object.entries(routes).forEach(([routeType, route]) => {
        let coordinates = [];

        // Handle different route formats
        if (route.coordinates) {
          coordinates = route.coordinates;
        } else if (route.points) {
          // GraphHopper encoded polyline
          if (typeof route.points === "string") {
            coordinates = decodePolyline(route.points);
          } else if (Array.isArray(route.points)) {
            coordinates = route.points;
          }
        }

        if (coordinates.length > 0) {
          const isSelected = routeType === (selectedRoute || recommendedRoute);
          const isRecommended = routeType === recommendedRoute;

          const routeLine = L.polyline(coordinates, {
            color: isSelected
              ? "#2196f3"
              : isRecommended
              ? "#4CAF50"
              : "#757575",
            weight: isSelected ? 6 : 4,
            opacity: isSelected ? 0.9 : 0.6,
            dashArray: isSelected ? null : "10, 10",
          }).addTo(map);

          // Popup mit Route-Info
          routeLine.bindPopup(`
            <div>
              <strong>${
                routeType === "fastest"
                  ? "🚗 Schnellste Route"
                  : routeType === "no_autobahn"
                  ? "🛣️ Ohne Autobahn"
                  : routeType === "shortest"
                  ? "📏 Kürzeste Route"
                  : routeType
              }</strong>
              ${
                isRecommended
                  ? '<br/><span style="color: green;">✓ Empfohlen</span>'
                  : ""
              }
              <br/>Zeit: ${Math.round(
                (route.traffic_adjustment?.adjusted_time || route.duration) / 60
              )} min
              <br/>Entfernung: ${(route.distance / 1000).toFixed(1)} km
              ${
                route.traffic_adjustment?.traffic_delays?.length > 0
                  ? `<br/>⚠️ ${route.traffic_adjustment.traffic_delays.length} Verkehrsstörung(en)`
                  : ""
              }
            </div>
          `);

          routeLines.push(routeLine);

          if (!bounds) {
            bounds = routeLine.getBounds();
          } else {
            bounds.extend(routeLine.getBounds());
          }
        }
      });

      if (bounds) {
        map.fitBounds(bounds, { padding: [20, 20] });
      }
    } else if (routes) {
      // Single route fallback
      let coordinates = [];

      if (routes.coordinates) {
        coordinates = routes.coordinates;
      } else if (routes.points) {
        if (typeof routes.points === "string") {
          coordinates = decodePolyline(routes.points);
        } else if (Array.isArray(routes.points)) {
          coordinates = routes.points;
        }
      }

      if (coordinates.length > 0) {
        const routeLine = L.polyline(coordinates, {
          color: "#2196f3",
          weight: 5,
          opacity: 0.7,
        }).addTo(map);

        routeLines.push(routeLine);
        map.fitBounds(routeLine.getBounds());
      }
    }

    return () => {
      routeLines.forEach((line) => map.removeLayer(line));
    };
  }, [routes, recommendedRoute, routeData, map]);

  return null;
}

// Komponente für Verkehrsstörungen entlang der Route
function RouteTrafficDelays({ route }) {
  const map = useMap();

  useEffect(() => {
    const markers = [];

    if (route?.traffic_adjustment?.traffic_delays) {
      route.traffic_adjustment.traffic_delays.forEach((delay, index) => {
        // Für Demo-Zwecke verwenden wir approximierte Positionen entlang der Route
        // In einer echten Implementierung würden wir die genauen Koordinaten der Störungen haben

        let coordinates = [];
        if (route.points && typeof route.points === "string") {
          coordinates = decodePolyline(route.points);
        }

        if (coordinates.length > 0) {
          // Verteile Störungen entlang der Route
          const segmentIndex = Math.floor(
            (coordinates.length /
              route.traffic_adjustment.traffic_delays.length) *
              index
          );
          const position =
            coordinates[Math.min(segmentIndex, coordinates.length - 1)];

          if (position) {
            const severityColor =
              delay.severity === "heavy"
                ? "#F44336"
                : delay.severity === "moderate"
                ? "#FF9800"
                : delay.severity.startsWith("incident_")
                ? "#9C27B0"
                : "#FFC107";

            const delayIcon = new L.DivIcon({
              html: `<div style="
                background-color: ${severityColor};
                width: 24px;
                height: 24px;
                border-radius: 50%;
                border: 3px solid white;
                box-shadow: 0 2px 6px rgba(0,0,0,0.3);
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 12px;
                color: white;
                font-weight: bold;
              ">⚠️</div>`,
              className: "traffic-delay-marker",
              iconSize: [24, 24],
              iconAnchor: [12, 12],
            });

            const marker = L.marker(position, { icon: delayIcon }).addTo(map);

            marker.bindPopup(`
              <div>
                <strong>🚨 Verkehrsstörung</strong><br/>
                <strong>Ort:</strong> ${delay.location}<br/>
                <strong>Art:</strong> ${
                  delay.severity === "heavy"
                    ? "Starker Verkehr"
                    : delay.severity === "moderate"
                    ? "Mäßiger Verkehr"
                    : delay.severity.startsWith("incident_")
                    ? "Verkehrsstörung"
                    : delay.severity
                }<br/>
                <strong>Verzögerung:</strong> +${Math.round(
                  delay.delay_seconds / 60
                )} min
                ${
                  delay.current_speed
                    ? `<br/><strong>Geschwindigkeit:</strong> ${delay.current_speed} km/h`
                    : ""
                }
              </div>
            `);

            markers.push(marker);
          }
        }
      });
    }

    return () => {
      markers.forEach((marker) => map.removeLayer(marker));
    };
  }, [route, map]);

  return null;
}

// Icons für verschiedene Incident-Typen
const getIncidentIcon = (type) => {
  const iconMap = {
    traffic_jam: { icon: Traffic, color: "#f44336" },
    accident: { icon: CarCrash, color: "#e91e63" },
    construction: { icon: Construction, color: "#ff9800" },
    road_closed: { icon: Close, color: "#9c27b0" },
    hazard: { icon: Warning, color: "#ff5722" },
  };

  return iconMap[type] || { icon: Warning, color: "#757575" };
};

function MapView({
  center = [51.1657, 10.4515], // Deutschland Zentrum
  zoom = 6,
  route = null,
  routes = null, // Neue Prop für Multiple Routes
  recommendedRoute = null, // Neue Prop für empfohlene Route
  selectedRoute = null, // Neue Prop für ausgewählte Route
  incidents = [],
  traffic = [], // Verkehrssegmente
  onMapClick = null,
  markers = [],
  currentPosition = null,
  isNavigating = false,
}) {
  const [selectedIncident, setSelectedIncident] = useState(null);

  // Komponente für aktuelle Position
  function CurrentPositionMarker({ position }) {
    const map = useMap();

    useEffect(() => {
      if (position && isNavigating) {
        // Zentriere Karte auf aktuelle Position während Navigation
        map.setView([position.lat, position.lng], 17, { animate: true });
      }
    }, [position, map]);

    if (!position) return null;

    const positionIcon = new L.DivIcon({
      html: `<div style="
        background: radial-gradient(circle, #2196f3 40%, rgba(33, 150, 243, 0.3) 70%);
        width: 20px;
        height: 20px;
        border-radius: 50%;
        border: 3px solid white;
        box-shadow: 0 2px 6px rgba(0,0,0,0.3);
      "></div>`,
      className: "current-position-marker",
      iconSize: [20, 20],
      iconAnchor: [10, 10],
    });

    return (
      <Marker position={[position.lat, position.lng]} icon={positionIcon}>
        <Popup>
          <div>
            <Typography variant="subtitle2">Aktuelle Position</Typography>
            <Typography variant="body2">
              {position.lat.toFixed(6)}, {position.lng.toFixed(6)}
            </Typography>
            <Typography variant="caption">
              Genauigkeit: ±{position.accuracy}m
            </Typography>
          </div>
        </Popup>
      </Marker>
    );
  }

  return (
    <Box sx={{ position: "relative", height: "100%", width: "100%" }}>
      <MapContainer
        center={center}
        zoom={zoom}
        style={{ height: "100%", width: "100%" }}
        onClick={onMapClick}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />

        {/* Neue Multi-Route Anzeige oder Fallback auf Single Route */}
        {routes ? (
          <MultiRouteDisplay
            routes={routes}
            recommendedRoute={recommendedRoute}
            selectedRoute={selectedRoute}
            routeData={route}
          />
        ) : route ? (
          <MultiRouteDisplay routes={route} />
        ) : null}

        {/* Verkehrsstörungen entlang der Route anzeigen */}
        {route && <RouteTrafficDelays route={route} />}

        {/* Aktuelle Position anzeigen */}
        {currentPosition && (
          <CurrentPositionMarker position={currentPosition} />
        )}

        {/* Standard Marker */}
        {markers.map((marker, index) => (
          <Marker key={index} position={[marker.lat, marker.lng]}>
            {marker.popup && (
              <Popup>
                <div>
                  <Typography variant="subtitle2">{marker.title}</Typography>
                  <Typography variant="body2">{marker.popup}</Typography>
                </div>
              </Popup>
            )}
          </Marker>
        ))}

        {/* Verkehrssegmente */}
        {traffic.map((segment, index) => {
          // Farbe basierend auf Geschwindigkeit
          const getSpeedColor = (speed, freeFlowSpeed) => {
            const ratio = speed / freeFlowSpeed;
            if (ratio > 0.8) return "#4CAF50"; // Grün - flüssig
            if (ratio > 0.6) return "#FF9800"; // Orange - langsam
            if (ratio > 0.3) return "#F44336"; // Rot - Stau
            return "#9C27B0"; // Violett - blockiert
          };

          const color = getSpeedColor(segment.speed, segment.freeFlowSpeed);
          const severity = segment.severity || "light";

          const trafficIcon = new L.DivIcon({
            html: `<div style="
              background-color: ${color};
              width: 16px;
              height: 16px;
              border-radius: 50%;
              border: 2px solid white;
              box-shadow: 0 1px 3px rgba(0,0,0,0.3);
              display: flex;
              align-items: center;
              justify-content: center;
            ">
              <div style="
                width: 8px;
                height: 8px;
                background-color: white;
                border-radius: 50%;
              "></div>
            </div>`,
            className: "traffic-segment-marker",
            iconSize: [16, 16],
            iconAnchor: [8, 8],
          });

          return (
            <Marker
              key={`traffic-${index}`}
              position={[segment.lat, segment.lng]}
              icon={trafficIcon}
            >
              <Popup>
                <div>
                  <Typography variant="subtitle2">
                    {segment.roadName || "Verkehrssegment"}
                  </Typography>
                  <Typography variant="body2">
                    Geschwindigkeit: {segment.speed} km/h
                    <br />
                    Tempolimit: {segment.freeFlowSpeed} km/h
                    <br />
                    Status:{" "}
                    {severity === "heavy"
                      ? "Stau"
                      : severity === "moderate"
                      ? "Langsam"
                      : "Flüssig"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Quelle: {segment.source}
                    <br />
                    {segment.timestamp &&
                      `Aktualisiert: ${new Date(
                        segment.timestamp
                      ).toLocaleTimeString()}`}
                  </Typography>
                </div>
              </Popup>
            </Marker>
          );
        })}

        {/* Verkehrsvorfälle */}
        {incidents.map((incident) => {
          const { color } = getIncidentIcon(incident.type);

          const customIcon = new L.DivIcon({
            html: `<div style="
              background-color: ${color};
              width: 24px;
              height: 24px;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              border: 2px solid white;
              box-shadow: 0 2px 4px rgba(0,0,0,0.3);
            ">
              <svg style="fill: white; width: 16px; height: 16px;" viewBox="0 0 24 24">
                ${getIconSvgPath(incident.type)}
              </svg>
            </div>`,
            className: "custom-incident-marker",
            iconSize: [24, 24],
            iconAnchor: [12, 12],
          });

          return (
            <Marker
              key={incident.id}
              position={[incident.lat, incident.lng]}
              icon={customIcon}
              eventHandlers={{
                click: () => setSelectedIncident(incident),
              }}
            />
          );
        })}
      </MapContainer>

      {/* Incident Details Popup */}
      {selectedIncident && (
        <Paper
          sx={{
            position: "absolute",
            bottom: 16,
            left: 16,
            right: 16,
            p: 2,
            zIndex: 1000,
            maxWidth: 400,
            mx: "auto",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 1,
            }}
          >
            <Typography variant="h6" component="div">
              {getIncidentTitle(selectedIncident.type)}
            </Typography>
            <IconButton
              size="small"
              onClick={() => setSelectedIncident(null)}
              sx={{ mt: -1, mr: -1 }}
            >
              <Close />
            </IconButton>
          </Box>

          <Chip
            label={selectedIncident.severity}
            color={getSeverityColor(selectedIncident.severity)}
            size="small"
            sx={{ mb: 1 }}
          />

          <Typography variant="body2" color="text.secondary">
            {selectedIncident.description}
          </Typography>

          {config.isDevelopment && (
            <Typography
              variant="caption"
              color="primary"
              sx={{ mt: 1, display: "block" }}
            >
              Demo-Daten
            </Typography>
          )}
        </Paper>
      )}

      {/* Demo Mode Indicator */}
      {config.isDevelopment && (
        <Chip
          label="Demo-Modus"
          color="warning"
          size="small"
          className="demo-indicator"
        />
      )}

      {/* Route & Traffic Legend */}
      {(routes || route?.traffic_adjustment?.traffic_delays) && (
        <Paper
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            p: 2,
            maxWidth: 250,
            zIndex: 1000,
            backgroundColor: "rgba(255, 255, 255, 0.95)",
          }}
        >
          <Typography variant="subtitle2" gutterBottom>
            🗺️ Karten-Legende
          </Typography>

          {routes && Object.keys(routes).length > 1 && (
            <>
              <Typography
                variant="caption"
                sx={{ fontWeight: "bold", display: "block", mt: 1 }}
              >
                Routen:
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
                <Box
                  sx={{
                    width: 20,
                    height: 3,
                    backgroundColor: "#2196f3",
                    mr: 1,
                  }}
                />
                <Typography variant="caption">Empfohlene Route</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
                <Box
                  sx={{
                    width: 20,
                    height: 3,
                    backgroundColor: "#757575",
                    mr: 1,
                    borderTop: "2px dashed #757575",
                    backgroundColor: "transparent",
                  }}
                />
                <Typography variant="caption">Alternative Routen</Typography>
              </Box>
            </>
          )}

          {route?.traffic_adjustment?.traffic_delays &&
            route.traffic_adjustment.traffic_delays.length > 0 && (
              <>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: "bold", display: "block", mt: 1 }}
                >
                  Verkehrsstörungen:
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      backgroundColor: "#F44336",
                      borderRadius: "50%",
                      mr: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "10px",
                    }}
                  >
                    ⚠️
                  </Box>
                  <Typography variant="caption">Starker Verkehr</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      backgroundColor: "#FF9800",
                      borderRadius: "50%",
                      mr: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "10px",
                    }}
                  >
                    ⚠️
                  </Box>
                  <Typography variant="caption">Mäßiger Verkehr</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      backgroundColor: "#9C27B0",
                      borderRadius: "50%",
                      mr: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "10px",
                    }}
                  >
                    ⚠️
                  </Box>
                  <Typography variant="caption">Störungen</Typography>
                </Box>
              </>
            )}
        </Paper>
      )}
    </Box>
  );
}

// Helper-Funktionen
function getIncidentTitle(type) {
  const titles = {
    traffic_jam: "Verkehrsstau",
    accident: "Unfall",
    construction: "Baustelle",
    road_closed: "Straße gesperrt",
    hazard: "Gefahr",
  };
  return titles[type] || "Verkehrsvorfall";
}

function getSeverityColor(severity) {
  const colors = {
    low: "success",
    medium: "warning",
    high: "error",
    critical: "error",
  };
  return colors[severity] || "default";
}

function getIconSvgPath(type) {
  const paths = {
    traffic_jam:
      '<path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.22.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/>',
    accident:
      '<path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.22.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/>',
    construction:
      '<path d="M13.783 15.172l2.121-2.121 5.996 5.996-2.121 2.121zM17.5 10c1.381 0 2.5-1.119 2.5-2.5 0-1.381-1.119-2.5-2.5-2.5s-2.5 1.119-2.5 2.5c0 1.381 1.119 2.5 2.5 2.5zM8.5 10c1.381 0 2.5-1.119 2.5-2.5 0-1.381-1.119-2.5-2.5-2.5s-2.5 1.119-2.5 2.5c0 1.381 1.119 2.5 2.5 2.5z"/>',
    road_closed:
      '<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>',
    hazard: '<path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>',
  };
  return paths[type] || paths.hazard;
}

export default MapView;
